import { useEffect, useMemo, useState } from 'react';
import type { EditorApi, QueryInfo } from '../types';

interface Props {
  api: EditorApi;
  project: string;
  onPick: (q: QueryInfo) => void;
  onClose: () => void;
}

export default function QueryPicker({ api, project, onPick, onClose }: Props) {
  const [queries, setQueries] = useState<QueryInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    api
      .listQueries(project)
      .then(setQueries)
      .catch(e => setError(e?.message ?? 'Failed to load queries'));
  }, [api, project]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (queries ?? []).filter(x => !q || x.path.toLowerCase().includes(q));
  }, [queries, filter]);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modal-head">
          <h2>Embed query results</h2>
          <p className="muted">Pick a saved query from <strong>{project}</strong>. Results render live for readers in Azure DevOps.</p>
          <input className="input" placeholder="Filter…" value={filter} onChange={e => setFilter(e.target.value)} autoFocus />
        </div>
        <div className="modal-body query-list">
          {error && <div className="notice notice-error">{error}</div>}
          {!error && queries === null && <div className="muted">Loading queries…</div>}
          {shown.map(q => (
            <button key={q.id} className="query-row" onClick={() => onPick(q)}>
              <span className="query-name">{q.name}</span>
              <span className="query-path">{q.path}{q.isPublic ? '' : ' · private'}</span>
            </button>
          ))}
          {queries && shown.length === 0 && <div className="muted">No queries match.</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
